// Creature Part Grammar — authoring layer.
// See /vault/claude/plans/amoeba-creature-grammar.md.
//
// A creature is authored as a declarative CreatureRecipe (a body plan + a set
// of ornaments + named params) and COMPILED by packRecipe() into the raw
// morphology floats the creature.slang Instance consumes. Authors never touch
// featureFlags bit math or the anonymous featureA/featureB scalars again — the
// packer is the single point where recipes become instance data, and thus the
// single place a new part can desync from the shader (guarded by the
// round-trip test in tests/creature-recipe.test.ts + the all-shader compile
// gate).
//
// A creature carries only two free scalars (count → featureA, length →
// featureB); a body and an ornament must not both consume the same one (see
// recipeHasScalarClash).

// ── Canonical FEAT_* mirror ─────────────────────────────────────────────────
// Single source of truth on the TS side for the bit values defined in
// src/shaders/_paper.slang. Keep in lockstep with that file. Body-plan bits
// (9–14) are a mutually-exclusive archetype enum by convention (the shader
// tests them as an if/else-if chain); ornament bits (0–8) compose freely.
export const FEAT = {
  CILIA: 1,          // bit 0  — paramecium cilia ring
  FLAGELLA: 2,       // bit 1  — flagellate whip tail(s)
  PSEUDO: 4,         // bit 2  — pseudopod lobes
  SPIKE_TEETH: 8,    // bit 3  — triangular edge spikes
  TENDRILS: 16,      // bit 4  — short outward bristles
  NUCLEUS_BIG: 32,   // bit 5  — enlarged offset nucleus
  CONTRACTILE: 64,   // bit 6  — contractile vacuole dot
  VACUOLES: 128,     // bit 7  — extra interior vacuoles
  TEAM_HALO: 256,    // bit 8  — "on your team" aura (allies)
  STAR: 512,         // bit 9  — Haeckel star (threat body)
  GRAIN: 1024,       // bit 10 — food grain (calm oriented ellipse body)
  PEAR: 2048,        // bit 11 — mother pear body
  GEM: 4096,         // bit 12 — DNA pickup gem body
  HEX: 8192,         // bit 13 — mutation pickup hex body
  MITO: 16384,       // bit 14 — mitochondrion bean body
  ROD: 32768,        // bit 15 — pill / rod body (capsule)
  SPIRAL: 65536,     // bit 16 — spirillum body (wavy rod)
  PLANT: 131072,     // bit 17 — plant cell body (rounded box)
  CELLWALL: 262144,  // bit 18 — rigid double-rim ornament
  DNA: 524288,       // bit 19 — interior double-helix ornament
  LEGS: 1048576,     // bit 20 — bent radiating legs (spider legs)
  VALUE: 2097152,    // bit 21 — warm-gold core + glint (value tell)
  MUSHROOM: 4194304, // bit 22 — cap-over-stalk body
  GILLS: 8388608,    // bit 23 — radial gill lines under the cap (LAST bit of int 1)
} as const

// Second flags int (featureFlags2) — the grammar budget continues here now that
// featureFlags (int 1) is full. Bit values restart at 1.
export const FEAT2 = {
  HYPHAE: 1,         // bit 0 — fine branching root threads
  // Not an ornament (absent from ORNAMENT_FLAG2): set at spawn on apex
  // predators (spawn-predators.ts). Drives the shader's self-lit danger rim +
  // pulsing nucleus. Mirror of _paper.slang's FEAT2_APEX.
  APEX: 2,           // bit 1 — apex predator threat tell
} as const

// ── Grammar vocabulary ──────────────────────────────────────────────────────

/** Mutually-exclusive base shape + symmetry skeleton (one per creature). */
export type BodyPlanId =
  | 'blob'      // default round body (no archetype bit)
  | 'grain'     // heading-oriented food ellipse
  | 'star'      // Haeckel needle-star (threat)
  | 'pear'      // mother
  | 'gem'       // DNA pickup
  | 'hex'       // mutation pickup
  | 'mito'      // endosymbiont orbiter
  | 'rod'       // pill / rod (capsule) — count unused, length elongates
  | 'spiral'    // spirillum (wavy rod) — count = wave humps
  | 'plantCell' // rounded box — pairs with the cellWall ornament
  | 'mushroom'  // upright cap-over-stalk — pairs with the gills ornament

/** Additive surface parts that stack on any body. */
export type OrnamentId =
  | 'cilia' | 'flagella' | 'pseudopod' | 'spikeTeeth' | 'tendrils'
  | 'nucleusBig' | 'contractile' | 'vacuoles' | 'teamHalo'
  | 'cellWall' | 'dnaStrand' | 'legs' | 'valueGlint' | 'gills' | 'hyphae'

const BODY_FLAG: Record<BodyPlanId, number> = {
  blob: 0,
  grain: FEAT.GRAIN,
  star: FEAT.STAR,
  pear: FEAT.PEAR,
  gem: FEAT.GEM,
  hex: FEAT.HEX,
  mito: FEAT.MITO,
  rod: FEAT.ROD,
  spiral: FEAT.SPIRAL,
  plantCell: FEAT.PLANT,
  mushroom: FEAT.MUSHROOM,
}

// Ornaments in the first flags int (featureFlags).
const ORNAMENT_FLAG: Partial<Record<OrnamentId, number>> = {
  cilia: FEAT.CILIA,
  flagella: FEAT.FLAGELLA,
  pseudopod: FEAT.PSEUDO,
  spikeTeeth: FEAT.SPIKE_TEETH,
  tendrils: FEAT.TENDRILS,
  nucleusBig: FEAT.NUCLEUS_BIG,
  contractile: FEAT.CONTRACTILE,
  vacuoles: FEAT.VACUOLES,
  teamHalo: FEAT.TEAM_HALO,
  cellWall: FEAT.CELLWALL,
  dnaStrand: FEAT.DNA,
  legs: FEAT.LEGS,
  valueGlint: FEAT.VALUE,
  gills: FEAT.GILLS,
}
// Ornaments in the second flags int (featureFlags2).
const ORNAMENT_FLAG2: Partial<Record<OrnamentId, number>> = {
  hyphae: FEAT2.HYPHAE,
}

/**
 * A creature as composed parts. `count` and `length` are the two per-creature
 * scalars the current instance can carry (they pack to featureA/featureB); each
 * body/ornament privately interprets them (cilia ring size, star fold count +
 * needle length, etc.).
 */
export interface CreatureRecipe {
  body: BodyPlanId
  ornaments?: OrnamentId[]
  /** Primary scalar → featureA (ring/fold/lobe count). */
  count?: number
  /** Secondary scalar → featureB (appendage length, etc.). */
  length?: number
  /** Third scalar → featureC (e.g. hyphae thread count). */
  count2?: number
  /** Fourth scalar → featureD (spare). */
  param4?: number
}

/** The packed morphology floats (sans seed, which is per-instance at spawn). */
export interface PackedMorphology {
  featureFlags: number
  featureA: number
  featureB: number
  featureFlags2: number
  featureC: number
  featureD: number
}

// ── Body-coupled behavior ───────────────────────────────────────────────────
// Behavior is a property of the BODY PLAN, not the named species (the design's
// "every body plan ships a behavior tell"). So a procedurally-rolled rod darts
// exactly like the hand-authored one. Bodies not listed use the generic wander.
export type CreatureBehaviorKind = 'darter' | 'driller' | 'vault'

const BODY_BEHAVIOR: Partial<Record<BodyPlanId, CreatureBehaviorKind>> = {
  rod: 'darter',
  spiral: 'driller',
  plantCell: 'vault',
}

export function behaviorForBody(body: BodyPlanId): CreatureBehaviorKind | undefined {
  return BODY_BEHAVIOR[body]
}

// Parts that read `count` (→ featureA). A recipe whose body AND an ornament
// both appear here would make them fight over the one scalar — an invalid
// combo. Authoring must avoid it; recipeHasScalarClash detects it in tests.
export const COUNT_BODIES: readonly BodyPlanId[] = ['spiral', 'star']
export const COUNT_ORNAMENTS: readonly OrnamentId[] =
  ['cilia', 'tendrils', 'legs', 'spikeTeeth', 'vacuoles', 'pseudopod']

/** True if the body and an ornament both consume `count` (featureA). */
export function recipeHasScalarClash(recipe: CreatureRecipe): boolean {
  if (!COUNT_BODIES.includes(recipe.body)) return false
  return (recipe.ornaments ?? []).some(o => COUNT_ORNAMENTS.includes(o))
}

/** Compile a recipe into the raw morphology floats the shader consumes. */
export function packRecipe(recipe: CreatureRecipe): PackedMorphology {
  let featureFlags = BODY_FLAG[recipe.body]
  let featureFlags2 = 0
  for (const orn of recipe.ornaments ?? []) {
    featureFlags |= ORNAMENT_FLAG[orn] ?? 0
    featureFlags2 |= ORNAMENT_FLAG2[orn] ?? 0
  }
  return {
    featureFlags,
    featureA: recipe.count ?? 0,
    featureB: recipe.length ?? 0,
    featureFlags2,
    featureC: recipe.count2 ?? 0,
    featureD: recipe.param4 ?? 0,
  }
}

/**
 * Full morphology object (packed floats + a caller-chosen seed), ready to drop
 * onto an entity. Most spawners want a random seed; the mother pins hers so her
 * silhouette is stable across sessions.
 */
export function morphologyFrom(recipe: CreatureRecipe, seed: number): {
  featureFlags: number; seed: number; featureA: number; featureB: number
  featureFlags2: number; featureC: number; featureD: number
} {
  return { ...packRecipe(recipe), seed }
}
